// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: luckyDrawCampaigns/$campaignId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Admin-managed lucky draw campaign windows for Danger Dan! Stores campaign start/end dates and active status to gate whether the lucky draw is currently open and ensure prizes are only distributed within the campaign window.
 */

export interface LuckyDrawCampaignsRequest {
  name: string;
  startAt: number | TimeOperation | IncrementOperation | TokenAmount;
  endAt: number | TimeOperation | IncrementOperation | TokenAmount;
  isActive: boolean;
  description?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface LuckyDrawCampaignsResponse {
  name: string;
  startAt: number;
  endAt: number;
  isActive: boolean;
  description?: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a LuckyDrawCampaigns operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildLuckyDrawCampaigns(campaignId: string, data: LuckyDrawCampaignsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `luckyDrawCampaigns/${campaignId}`, document: doc };
}

/**
 * Admin or founder only. Creates a campaign with name, startAt (Unix seconds), endAt (Unix seconds), isActive flag, optional description, and createdAt timestamp. Only createdAt is immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setLuckyDrawCampaigns(campaignId: string, data: LuckyDrawCampaignsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`luckyDrawCampaigns/${campaignId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting LuckyDrawCampaigns: ${formatError(error)}`);
    return false;
  }
}

export type LuckyDrawCampaignsRequestUpdate = Partial<LuckyDrawCampaignsRequest>;

/**
 * Build a LuckyDrawCampaigns update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateLuckyDrawCampaigns(campaignId: string, data: LuckyDrawCampaignsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `luckyDrawCampaigns/${campaignId}`, document: doc };
}

/**
 * Admin or founder only. Can update name, startAt, endAt, isActive, and description. createdAt cannot be changed (immutable). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateLuckyDrawCampaigns(campaignId: string, data: LuckyDrawCampaignsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`luckyDrawCampaigns/${campaignId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating LuckyDrawCampaigns: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read campaigns. Frontend uses this to check whether the lucky draw is currently open (isActive=true AND current time is between startAt and endAt).
   (Get Single Item)
 */
export async function getLuckyDrawCampaigns(campaignId: string): Promise<LuckyDrawCampaignsResponse | null> {
  try {
    const result = await get(`luckyDrawCampaigns/${campaignId}`);
    return result as LuckyDrawCampaignsResponse | null;
  } catch (error) {
    console.error(`Error getting LuckyDrawCampaigns: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single LuckyDrawCampaigns document. (
  Read Operation Details: Anyone can read campaigns. Frontend uses this to check whether the lucky draw is currently open (isActive=true AND current time is between startAt and endAt).
  )
 */
export function subscribeLuckyDrawCampaigns(callback: (data: LuckyDrawCampaignsResponse | null) => void, campaignId: string): Promise<() => Promise<void>> {
  return subscribe(`luckyDrawCampaigns/${campaignId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as LuckyDrawCampaignsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to LuckyDrawCampaigns at path '${`luckyDrawCampaigns/${campaignId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many LuckyDrawCampaigns items from collection luckyDrawCampaigns
 
  Read Operation Details: Anyone can read campaigns. Frontend uses this to check whether the lucky draw is currently open (isActive=true AND current time is between startAt and endAt).
  
 */
export async function getManyLuckyDrawCampaigns(filter: string = ""): Promise<LuckyDrawCampaignsResponse[]> {
  try {
    const results = await get("luckyDrawCampaigns", { prompt: filter });
    return Array.isArray(results) ? results as LuckyDrawCampaignsResponse[] : [];
  } catch (error) {
    console.error(`Error getting LuckyDrawCampaigns collection at path 'luckyDrawCampaigns': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LuckyDrawCampaigns collection at luckyDrawCampaigns
 
  Read Operation Details: Anyone can read campaigns. Frontend uses this to check whether the lucky draw is currently open (isActive=true AND current time is between startAt and endAt).
  
 */
export function subscribeManyLuckyDrawCampaigns(callback: (data: LuckyDrawCampaignsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("luckyDrawCampaigns", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LuckyDrawCampaignsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LuckyDrawCampaigns collection at path '${"luckyDrawCampaigns"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all LuckyDrawCampaigns items from collection luckyDrawCampaigns
 
  Read Operation Details: Anyone can read campaigns. Frontend uses this to check whether the lucky draw is currently open (isActive=true AND current time is between startAt and endAt).
  
 */
export async function getAllLuckyDrawCampaigns(filter: string = ""): Promise<LuckyDrawCampaignsResponse[]> {
  try {
    const results = await get("luckyDrawCampaigns", { prompt: filter });
    return Array.isArray(results) ? results as LuckyDrawCampaignsResponse[] : [];
  } catch (error) {
    console.error(`Error getting LuckyDrawCampaigns collection at path 'luckyDrawCampaigns': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LuckyDrawCampaigns collection at luckyDrawCampaigns
 
  Read Operation Details: Anyone can read campaigns. Frontend uses this to check whether the lucky draw is currently open (isActive=true AND current time is between startAt and endAt).
  
 */
export function subscribeAllLuckyDrawCampaigns(callback: (data: LuckyDrawCampaignsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("luckyDrawCampaigns", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LuckyDrawCampaignsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LuckyDrawCampaigns collection at path '${"luckyDrawCampaigns"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count LuckyDrawCampaigns items in collection luckyDrawCampaigns.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countLuckyDrawCampaigns(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("luckyDrawCampaigns", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting LuckyDrawCampaigns at path 'luckyDrawCampaigns': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on LuckyDrawCampaigns items in collection luckyDrawCampaigns.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateLuckyDrawCampaigns(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("luckyDrawCampaigns", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating LuckyDrawCampaigns at path 'luckyDrawCampaigns': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove a campaign.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteLuckyDrawCampaigns(campaignId: string): Promise<boolean> {
  try {
    const succeeded = await set(`luckyDrawCampaigns/${campaignId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting LuckyDrawCampaigns: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for LuckyDrawCampaigns for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteLuckyDrawCampaigns(campaignId: string): DocumentOperation {
  return { path: `luckyDrawCampaigns/${campaignId}`, document: null };
}

